import ThemedSelect from "../components/shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Form, Modal, Table } from "react-bootstrap";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { ClaimLink, money } from "../components/reports/reportShared.jsx";
import { findInvoices, isAbortError, listEntityRows, listUsers, saveInvoiceChaseComment } from "../services/apiClient.js";
import { formatDate } from "../utils/date.js";
import { FaSave } from "react-icons/fa";

// Find Invoice — 1.0's InvoicesSearch.aspx. First built lean (2026-07-17),
// brought to 1.0 PARITY 2026-08-24 (Robert, with the 1.0 screenshot): this is
// Kaaren Vodanovich's page, and she works ONE DAY A WEEK — which is why two
// months of live surfaced no requests about it. The additions are the
// claim-side dropdowns (insurer / assessor / assessor co / PCO / division),
// 1.0's Paid and Approved Yes-No-All radios, the approved-date range, and
// Export CSV in place of Export To Excel.
//
// Deliberately NOT ported: the Hide Columns checkbox row. It existed to trim
// 1.0's Excel export; the CSV here carries every column and spreadsheets
// delete columns better than search pages hide them.
//
// "Approved" is external_approval — the assessor's approval Accounts chases —
// which is what 1.0's column meant.

const INVOICE_TYPES = [
  "Quoted Invoice", "Final Quoted Invoice", "Progress Claim",
  "Excess Invoice", "Quote Fee", "Other Invoice",
];

// REMEMBERED PER TAB (KS 2026-09-09: "when you find an invoice and go back to
// that page it remembers what you had selected last time"), the way the main
// search does (ClaimSearchPage, sessionStorage): the filters, the mode radio
// and the fact that a search was run, so coming back re-runs it and the rows
// are there. Clear forgets. sessionStorage, so two tabs never fight and a
// new browser starts clean.
const STATE_KEY = "findInvoice:last:v1";
function loadSaved() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STATE_KEY) || "null");
    if (!saved || typeof saved !== "object") return null;
    return { filters: { ...EMPTY, ...(saved.filters || {}) }, mode: saved.mode === "contractor" ? "contractor" : "invoices", searched: !!saved.searched };
  } catch {
    return null;
  }
}
function saveState(filters, mode, searched) {
  try {
    sessionStorage.setItem(STATE_KEY, JSON.stringify({ filters, mode, searched }));
  } catch {
    /* private mode */
  }
}

const EMPTY = {
  no_from: "", no_to: "", job: "", insurance_claim: "", invoice_type: "",
  from: "", to: "",
  insurer_id: "", assessor_id: "", assessor_company_id: "", pco_id: "", division_id: "",
  paid: "", approved: "", approved_from: "", approved_to: "",
  // 1.0's ddlInvoiceTypeOperator / ddlDivisionOperator — Equals or Excludes,
  // opening on Equals. Only these two filters have one, as in 1.0.
  invoice_type_op: "equals", division_op: "equals",
  // The contractor-invoice search's own two fields (1.0's txtContractorInvoice
  // + ddlContractor), which live behind the mode radio rather than among the
  // invoice filters.
  contractor_invoice_no: "", contractor_company_id: "",
};

// The CSV's columns — 1.0's Hide Columns list, one entry per tickbox. The
// picker works off this registry, so adding a column here adds it to the
// export AND the modal.
const EXPORT_COLUMNS = [
  { key: "invoice_no", label: "Invoice #", value: (r) => r.invoice_no },
  { key: "job_no", label: "Job #", value: (r) => r.claim_number },
  { key: "client", label: "Client", value: (r) => r.client_name },
  { key: "insurer", label: "Insurer", value: (r) => r.insurer_name },
  { key: "division", label: "Division", value: (r) => r.division_name },
  { key: "job_type", label: "Job Type", value: (r) => r.job_type_name },
  { key: "type", label: "Type", value: (r) => r.invoice_type },
  { key: "date", label: "Date", value: (r) => r.invoice_date },
  { key: "excl_gst", label: "Excl GST", value: (r) => r.subtotal },
  { key: "gst", label: "GST", value: (r) => r.gst },
  { key: "incl_gst", label: "Incl GST", value: (r) => r.total },
  { key: "paid", label: "Paid", value: (r) => (r.has_been_paid ? "Yes" : "No") },
  { key: "approved", label: "Approved", value: (r) => (r.external_approval ? "Yes" : "No") },
  { key: "approved_date", label: "Approved Date", value: (r) => r.approved_date },
];

// One fetch per page load, shared: the five lists are tiny and static.
const LOOKUP_KEYS = ["insurer-companies", "assessors", "assessor-companies", "divisions", "contractor-companies"];

// 1.0's Yes / No / All radios. "" = All = no filter, the default. The LABEL
// is the grid's, not this component's — see the filter grid below.
function TriState({ label, value, onChange }) {
  return (
    <div className="d-flex align-items-center column-gap-2">
      {[["yes", "Yes"], ["no", "No"], ["", "All"]].map(([v, text]) => (
        <Form.Check key={text} inline type="radio" id={`${label}-${text}`} label={text}
          className="me-0 small" checked={value === v} onChange={() => onChange(v)} />
      ))}
    </div>
  );
}

// FIELD PIECES LIVE AT MODULE LEVEL, NOT INSIDE THE PAGE (Robert 2026-08-28:
// "all textboxs only allow 1 char before it loses focus"). A component created
// inside a render has a NEW identity on every render, so React unmounts the
// old input and mounts a fresh one after each keystroke — the caret goes with
// it. Defined out here they are stable, and only their props change.
//
// LABEL BESIDE CONTROL, three columns, matching 1.0's InvoicesSearch: "label,
// control, not label top control on the bottom". Each column sizes its own
// label gutter (grid max-content), so the short column does not inherit
// "Project Co-Ordinator"'s width.
function Field({ label, children }) {
  return (
    <>
      <Form.Label>{label}</Form.Label>
      <div className="d-flex align-items-center column-gap-1">{children}</div>
    </>
  );
}
// The contractor form is three short fields, so they sit on ONE line as 1.0's
// does (Robert 2026-08-28: "i'm sure we can put some on same line, if space"),
// wrapping only when the window is too narrow to hold them.
function InlineField({ label, width, children }) {
  return (
    <div className="d-flex align-items-center column-gap-2" style={width ? { width } : undefined}>
      <Form.Label className="filter-label mb-0">{label}</Form.Label>
      {children}
    </div>
  );
}
function Txt({ value, onChange }) {
  return <Form.Control size="sm" style={{ flex: 1, minWidth: 0 }} value={value} onChange={onChange} />;
}
function Dt({ value, onChange }) {
  return <Form.Control size="sm" type="date" style={{ flex: 1, minWidth: 0 }} value={value} onChange={onChange} />;
}
function Sel({ value, onChange, options, labelField = "name", all = "All", fill = true }) {
  // Fills its column like the text boxes do (Robert 2026-09-09: the middle
  // column's dropdowns sat at "All" width under a full-width Insurance claim #).
  // fill={false} keeps a dropdown at its natural width — the Division value
  // beside its Equals / Excludes operator reads better short, like Invoice Type.
  return (
    <ThemedSelect size="sm" style={fill ? { flex: 1, minWidth: 0 } : undefined} value={value} onChange={onChange}>
      <option value="">{all}</option>
      {(options || []).map((o) => <option key={o.id} value={o.id}>{o[labelField]}</option>)}
    </ThemedSelect>
  );
}
// 1.0's operator dropdown: Equals (default) or Excludes, and only on the two
// filters 1.0 gives one to (ddlInvoiceTypeOperator / ddlDivisionOperator).
function Op({ value, onChange }) {
  return (
    <ThemedSelect size="sm" style={{ width: "13ch", flexShrink: 0 }} value={value} onChange={onChange}>
      <option value="equals">Equals</option>
      <option value="excludes">Excludes</option>
    </ThemedSelect>
  );
}

function FindInvoicePage() {
  const [saved] = useState(loadSaved);
  const [filters, setFilters] = useState(saved?.filters || EMPTY);
  const [lookups, setLookups] = useState({});
  const [pcos, setPcos] = useState([]);
  const [result, setResult] = useState(null);
  // WHICH SEARCH (1.0's rblInvoiceSearchType radio): invoices, or contractor
  // invoices. They are two different searches over two different tables with
  // two different result grids, which is why 1.0 gives each its own form and
  // its own Search button. 2.0 used to infer the mode from whether one box in
  // the middle of the invoice filters had text in it — so typing there
  // silently changed what every other field meant.
  const [mode, setMode] = useState(saved?.mode || "invoices");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Coming back to the page: the last search runs again so the rows are
  // there, not just the boxes.
  useEffect(() => {
    if (saved?.searched) search(); // eslint-disable-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // House rule: the signal actually reaches the requests, and a failed load
    // SAYS so — silently empty dropdowns read as "no insurers", which is a
    // filter that quietly finds nothing.
    const controller = new AbortController();
    Promise.all(
      LOOKUP_KEYS.map((k) =>
        listEntityRows(k, { signal: controller.signal }).then((rows) => [k, rows])
      )
    )
      .then((pairs) => setLookups(Object.fromEntries(pairs)))
      .catch((err) => {
        if (!isAbortError(err)) setError("Some filter lists failed to load — reload the page to retry.");
      });
    listUsers({ pco: true, activeOnly: true, signal: controller.signal })
      .then(setPcos)
      .catch((err) => {
        if (!isAbortError(err)) setError("Some filter lists failed to load — reload the page to retry.");
      });
    return () => controller.abort();
  }, []);

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  // The chase comment per row (Kaaren; 1.0 parity): type, save with the disk
  // icon, and the row's history updates in place — each save stacks the new
  // line on top. It is a real progress entry, so the job's Progress tab has
  // it too, with author and date.
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  async function saveComment(row) {
    const text = (drafts[row.invoice_id] || "").trim();
    if (!text) return;
    setSavingId(row.invoice_id);
    setError("");
    try {
      const r = await saveInvoiceChaseComment(row.invoice_id, text);
      setResult((res) => ({
        ...res,
        rows: res.rows.map((x) => (x.invoice_id === row.invoice_id ? { ...x, chase_comments: r.chase_comments } : x)),
      }));
      setDrafts((d) => ({ ...d, [row.invoice_id]: "" }));
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save the comment.");
    } finally {
      setSavingId(null);
    }
  }
  const setVal = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  async function search(e) {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      // Only the active mode's fields go to the server, so a leftover value
      // in the other form cannot bend the search that is actually running.
      const mine = mode === "contractor"
        ? ["contractor_invoice_no", "contractor_company_id", "job"]
        : Object.keys(filters).filter((k) => !k.startsWith("contractor_"));
      const params = Object.fromEntries(
        Object.entries(filters).filter(([k, v]) => mine.includes(k) && v !== "")
      );
      // The radio decides, not the boxes: an empty contractor form must still
      // search CONTRACTOR invoices, or it silently answers with invoices.
      if (mode === "contractor") params.mode = "contractor";
      setResult(await findInvoices(params));
      saveState(filters, mode, true);
    } catch (err) {
      setError(err.response?.data?.error || "Search failed.");
    } finally {
      setBusy(false);
    }
  }

  // Export To Excel, the 2.0 way: the rows on screen as CSV — but through a
  // COLUMN-PICKER MODAL first (Robert 2026-08-24: 1.0's Hide Columns row
  // existed for this export and "apparently they use everything", so the
  // question is asked at export time where it belongs — an extra click is
  // nothing). Unticked columns are remembered per browser, so the second
  // export is configured before the modal opens.
  const [exporting, setExporting] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("findInvoice:exportHidden:v1") || "[]"));
    } catch {
      return new Set();
    }
  });
  const toggleColumn = (key) =>
    setHidden((h) => {
      const next = new Set(h);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem("findInvoice:exportHidden:v1", JSON.stringify([...next])); } catch { /* private mode */ }
      return next;
    });

  function exportCsv() {
    if (!result || result.mode !== "invoice") return;
    const cols = EXPORT_COLUMNS.filter((c) => !hidden.has(c.key));
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = result.rows.map((r) => cols.map((c) => esc(c.value(r))).join(","));
    const blob = new Blob([[cols.map((c) => esc(c.label)).join(","), ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "invoice-search.csv";
    a.click();
    URL.revokeObjectURL(a.href);
    setExporting(false);
  }

  const buttons = (
    <>
      <Button size="sm" variant="outline-secondary"
        onClick={() => {
          setFilters(EMPTY); setResult(null);
          try { sessionStorage.removeItem(STATE_KEY); } catch { /* ignore */ } // Clear = forget this tab's search
        }}>Clear</Button>
      {/* Shown only once there are rows to export (Robert 2026-08-28).
          Disabled, it rendered as an EMPTY BOX on this theme — the label
          vanished entirely — and a permanently greyed control before the
          first search says nothing anyway. */}
      {result?.mode === "invoice" && result.rows.length ? (
        <Button size="sm" variant="outline-secondary"
          onClick={() => setExporting(true)}>Export CSV</Button>
      ) : null}
      <Button size="sm" type="submit" disabled={busy} style={{ minWidth: 90 }}>Search</Button>
    </>
  );

  return (
    <section className="page-panel">
      <div className="page-heading"><h2>Find Invoice <RulesHelp topic="findInvoice" /></h2></div>

      <Form onSubmit={search} className="no-print">
        <Card className="mb-3">
          <Card.Body className="py-3">
            {/* 1.0's rblInvoiceSearchType, at the top where it belongs: the two
                searches are different questions over different tables. */}
            <div className="d-flex align-items-center column-gap-3 mb-3">
              {[["invoices", "Invoices"], ["contractor", "Contractor Invoices"]].map(([v, text]) => (
                <Form.Check key={v} inline type="radio" id={`mode-${v}`} label={text}
                  className="me-0 fw-semibold small" checked={mode === v}
                  onChange={() => { setMode(v); setResult(null); }} />
              ))}
            </div>

            {mode === "contractor" ? (
              /* Its own small form, as 1.0 has it — a number, a contractor, or
                 both. Neither is required on its own. */
              <div className="d-flex flex-wrap align-items-center column-gap-4 row-gap-2">
                <InlineField label="Invoice #" width="16rem">
                  <Txt value={filters.contractor_invoice_no} onChange={set("contractor_invoice_no")} />
                </InlineField>
                {/* Not in 1.0's contractor form, added because the grid prints
                    the job and you could not search by it. */}
                <InlineField label="Job #" width="12rem">
                  <Txt value={filters.job} onChange={set("job")} />
                </InlineField>
                <InlineField label="Contractor" width="26rem">
                  <Sel value={filters.contractor_company_id} onChange={set("contractor_company_id")} options={lookups["contractor-companies"]} />
                </InlineField>
              </div>
            ) : (
            <div className="filter-grid">
              {/* The invoice's own numbers and dates. */}
              <div className="filter-col">
                {/* From alone = exactly that invoice; both = the range (1.0's
                    behaviour, Robert 2026-08-25). */}
                <Field label="Invoice #">
                  <Txt value={filters.no_from} onChange={set("no_from")} />
                  <span className="small text-muted px-1">to</span>
                  <Txt value={filters.no_to} onChange={set("no_to")} />
                </Field>
                <Field label="Dated"><Dt value={filters.from} onChange={set("from")} /><span className="small text-muted px-1">to</span><Dt value={filters.to} onChange={set("to")} /></Field>
                <Field label="Approved"><Dt value={filters.approved_from} onChange={set("approved_from")} /><span className="small text-muted px-1">to</span><Dt value={filters.approved_to} onChange={set("approved_to")} /></Field>
                <Field label="Job #"><Txt value={filters.job} onChange={set("job")} /></Field>
              </div>

              {/* Whose job it hangs off. */}
              <div className="filter-col">
                <Field label="Insurance claim #"><Txt value={filters.insurance_claim} onChange={set("insurance_claim")} /></Field>
                <Field label="Insurer"><Sel value={filters.insurer_id} onChange={set("insurer_id")} options={lookups["insurer-companies"]} /></Field>
                <Field label="Current Assessor"><Sel value={filters.assessor_id} onChange={set("assessor_id")} options={lookups["assessors"]} /></Field>
                <Field label="Assessor Co"><Sel value={filters.assessor_company_id} onChange={set("assessor_company_id")} options={lookups["assessor-companies"]} /></Field>
                <Field label="Project Co-Ordinator"><Sel value={filters.pco_id} onChange={set("pco_id")} options={pcos} labelField="full_name" /></Field>
              </div>

              {/* State, and the two filters that carry an operator. */}
              <div className="filter-col">
                <Field label="Paid"><TriState label="Paid" value={filters.paid} onChange={setVal("paid")} /></Field>
                <Field label="Approved"><TriState label="Approved" value={filters.approved} onChange={setVal("approved")} /></Field>
                <Field label="Division">
                  <Op value={filters.division_op} onChange={set("division_op")} />
                  <Sel value={filters.division_id} onChange={set("division_id")} options={lookups["divisions"]} fill={false} />
                </Field>
                <Field label="Invoice Type">
                  <Op value={filters.invoice_type_op} onChange={set("invoice_type_op")} />
                  <ThemedSelect size="sm" value={filters.invoice_type} onChange={set("invoice_type")}>
                    <option value="">All types</option>
                    {INVOICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </ThemedSelect>
                </Field>
                {/* The buttons ride the column's spare last slot, level with
                    Project Co-Ordinator opposite (Robert 2026-08-28) — column 3
                    is a row shorter than column 2, so a whole extra row under
                    the panel was buying nothing. Spans the label gutter too. */}
                <div className="filter-actions d-flex justify-content-end column-gap-2 pt-1">
                  {buttons}
                </div>
              </div>
            </div>
            )}

            {/* Contractor mode has no spare slot, so its buttons keep a row. */}
            <div className={`d-flex justify-content-end column-gap-2 mt-3${mode === "contractor" ? "" : " d-none"}`}>
              <Button size="sm" variant="outline-secondary"
                onClick={() => { setFilters(EMPTY); setResult(null); }}>Clear</Button>
              {/* Shown only once there are rows to export (Robert 2026-08-28).
                  Disabled, it rendered as an EMPTY BOX on this theme — the
                  label vanished entirely — and a permanently greyed control
                  before the first search says nothing anyway. */}
              {result?.mode === "invoice" && result.rows.length ? (
                <Button size="sm" variant="outline-secondary"
                  onClick={() => setExporting(true)}>Export CSV</Button>
              ) : null}
              <Button size="sm" type="submit" disabled={busy} style={{ minWidth: 90 }}>Search</Button>
            </div>
          </Card.Body>
        </Card>
      </Form>

      {error ? <Alert variant="danger" className="py-2">{error}</Alert> : null}

      {result?.mode === "invoice" ? (
        <Table striped bordered hover responsive size="sm" className="align-middle">
          <thead className="table-surface">
            <tr><th>Invoice #</th><th>Job #</th><th>Client</th><th>Insurer</th><th>Division</th><th>Type</th><th>Date</th>
              <th className="text-end">Total</th><th>State</th><th style={{ minWidth: 260 }}>Comments</th></tr>
          </thead>
          <tbody>
            {result.rows.map((r) => (
              <tr key={r.invoice_id}>
                <td>{r.invoice_no}</td>
                <td><ClaimLink claimId={r.claim_id} claimNumber={r.claim_number} tab="invoicing" /></td>
                <td>{r.client_name}</td>
                <td>{r.insurer_name || "-"}</td>
                <td>{r.division_name || "-"}</td>
                <td>{r.invoice_type}</td>
                <td className="text-nowrap">{r.invoice_date}</td>
                <td className="text-end">{money(r.total)}</td>
                <td>
                  {r.has_been_paid ? <Badge bg="success" className="me-1">Paid</Badge> : <Badge bg="secondary" className="me-1">Unpaid</Badge>}
                  {r.external_approval ? <Badge bg="info" className="me-1" data-hint={r.approved_date || ""}>Approved</Badge> : null}
                  {r.disputed ? <Badge bg="danger" className="me-1">Disputed</Badge> : null}
                  {r.is_final ? <Badge bg="dark">Final</Badge> : null}
                </td>
                <td>
                  {/* Box FIRST, history under it (Robert 2026-08-25): the text
                      accumulates newest-first, so a saved comment appears
                      right below the box — where the eye already is. */}
                  <div className="d-flex align-items-center gap-1">
                    <input
                      className="form-control form-control-sm"
                      placeholder="Add comment…"
                      value={drafts[r.invoice_id] || ""}
                      onChange={(e) => setDrafts((d) => ({ ...d, [r.invoice_id]: e.target.value }))}
                      onKeyDown={(e) => {
                        // Same guard as the disk button: no blank saves, and no
                        // double-POST from a second Enter while one is in flight.
                        if (e.key === "Enter" && savingId !== r.invoice_id && (drafts[r.invoice_id] || "").trim()) saveComment(r);
                      }}
                    />
                    <Button size="sm" variant="outline-primary" data-hint="Save comment" aria-label="Save comment"
                      disabled={savingId === r.invoice_id || !(drafts[r.invoice_id] || "").trim()}
                      onClick={() => saveComment(r)}>
                      <FaSave />
                    </Button>
                  </div>
                  {r.chase_comments ? (
                    <div className="small mt-1" style={{ whiteSpace: "pre-wrap", maxHeight: 96, overflowY: "auto" }}>
                      {r.chase_comments}
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
            {result.rows.length === 0 ? <tr><td colSpan={10} className="text-center text-muted">No invoices match.</td></tr> : null}
          </tbody>
        </Table>
      ) : null}

      {result?.mode === "contractor" ? (
        <Table striped bordered hover responsive size="sm" className="align-middle">
          <thead className="table-surface">
            {/* 1.0's contractor grid, column for column and in its order
                (InvoicesSearch.aspx): Contractor Invoice #, Job Number,
                Invoice Amount, Contractor, Order #, Date, Approved. */}
            <tr><th>Contractor Invoice #</th><th>Job Number</th><th className="text-end">Invoice Amount</th>
              <th>Contractor</th><th>Order #</th><th>Date</th><th>Approved</th></tr>
          </thead>
          <tbody>
            {result.rows.map((r, i) => (
              <tr key={i}>
                <td>{r.invoice_number}</td>
                <td><ClaimLink claimId={r.claim_id} claimNumber={r.claim_number} tab="orders" /></td>
                <td className="text-end">{money(r.amount)}</td>
                <td>{r.contractor_name || "-"}</td>
                <td>{r.order_number || "-"}</td>
                <td>{formatDate(r.invoice_date) || "-"}</td>
                <td>{r.approved ? "Yes" : <span className="text-danger">No</span>}</td>
              </tr>
            ))}
            {result.rows.length === 0 ? <tr><td colSpan={7} className="text-center text-muted">No contractor invoices match.</td></tr> : null}
          </tbody>
        </Table>
      ) : null}
      {result && result.rows.length >= 250 ? (
        <div className="text-muted small">Showing the newest 250 invoices — narrow the search for older ones.</div>
      ) : null}

      {/* 1.0's Hide Columns row, asked at the moment it matters. Ticked =
          in the file; the choice sticks (this browser) for next time. */}
      <Modal show={exporting} onHide={() => setExporting(false)} size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="h6">Columns to export</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          {EXPORT_COLUMNS.map((c) => (
            <Form.Check key={c.key} type="checkbox" id={`export-${c.key}`} label={c.label}
              checked={!hidden.has(c.key)} onChange={() => toggleColumn(c.key)} />
          ))}
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button size="sm" variant="outline-secondary" onClick={() => setExporting(false)}>Cancel</Button>
          <Button size="sm" onClick={exportCsv} disabled={hidden.size >= EXPORT_COLUMNS.length}>
            Export {EXPORT_COLUMNS.length - hidden.size} column{EXPORT_COLUMNS.length - hidden.size === 1 ? "" : "s"}
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}

export default FindInvoicePage;
